<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'

import ModalBox from '@/components/modals/ModalBox.vue'
import TabGroup from '@/components/Atoms/TabGroup.vue'
import TabItem from '@/components/Atoms/TabItem.vue'
import AddWithFeedback from '@/components/Molecules/AddWithFeedback.vue'
import Badge from '@/components/Badge.vue'
import PlayoutFilesDirectoryRow from './PlayoutFilesDirectoryRow.vue'
import PlayoutPagination from './PlayoutPagination.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconLivestreamCamera from '@/components/icon/IconLivestreamCamera.vue'
import IconCheckmark from '@/components/icon/IconCheckmark.vue'

const LIVESTREAM_PAGE_SIZE = 5
const DIRECTORY_PAGE_SIZE = 8

const props = defineProps({
	visible: { type: Boolean, default: false },
	playoutId: { type: String, default: null },
	uploadDirs: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:visible', 'add-file', 'add-stream'])

const store = useStore()
const { notify } = useNotification()

const internalVisible = computed({
	get: () => props.visible,
	set: (v) => emit('update:visible', v),
})

const activeTab = ref('livestreams')
const page = ref(1)

const allInOneStreams = computed(() => store.getters['Streams/allInOneStreams'] || [])
const vodStreams = computed(() => store.getters['Streams/vodStreams'] || [])
const playlist = computed(() => store.getters['Playout/playlist'] || [])
const playoutConfig = computed(() => store.getters['Playout/playoutConfig'])

// Livestreams list — every all-in-one stream available as a source.
const livestreamsList = computed(() => allInOneStreams.value)

// Uploaded videos are organized into a folder hierarchy that mirrors the Files
// tab: playout upload directories plus live-to-vod streams (shown as folders).
// Each folder lazily loads its own files when expanded (handled by the row).
const liveToVodFolders = computed(() =>
	vodStreams.value.filter((stream) => stream.liveParent && (stream.totalFiles ?? 0) > 0),
)

const playoutDirs = computed(() => {
	const list = props.uploadDirs || []
	return [...list].reverse()
})

const allDirectories = computed(() => {
	const playoutEntries = playoutDirs.value.map((dir) => ({
		key: 'p-' + (dir.id || dir._id || dir.name),
		dir,
		isLiveToVod: false,
	}))
	const liveEntries = liveToVodFolders.value.map((dir) => ({
		key: 'l-' + (dir._id || dir.id || dir.name),
		dir,
		isLiveToVod: true,
	}))
	return [...playoutEntries, ...liveEntries]
})

const hasDirectories = computed(() => allDirectories.value.length > 0)

// Reset state when the modal opens.
watch(
	() => props.visible,
	(open) => {
		if (open) {
			activeTab.value = 'livestreams'
			page.value = 1
		}
	},
	{ immediate: true },
)

watch(activeTab, () => {
	page.value = 1
})

// How many times this livestream already appears in the schedule.
const streamAddCount = (stream) =>
	playlist.value.filter((item) => item.sourceLocation === stream.key).length

// Only block the stream that is currently set as this playout's destination.
const streamDisabled = (stream) =>
	!!playoutConfig.value?.destination &&
	stream._id === playoutConfig.value?.destination

// Pagination — page size depends on the active tab (livestreams paginate in
// smaller chunks; directories show 8 folders per page). Both tabs share the
// same `page` state, which resets on tab switch.
const totalPages = computed(() => {
	if (activeTab.value === 'livestreams') {
		return Math.max(1, Math.ceil(livestreamsList.value.length / LIVESTREAM_PAGE_SIZE))
	}
	return Math.max(1, Math.ceil(allDirectories.value.length / DIRECTORY_PAGE_SIZE))
})

const pagedLivestreams = computed(() => {
	const start = (page.value - 1) * LIVESTREAM_PAGE_SIZE
	return livestreamsList.value.slice(start, start + LIVESTREAM_PAGE_SIZE)
})

const pagedDirectories = computed(() => {
	const start = (page.value - 1) * DIRECTORY_PAGE_SIZE
	return allDirectories.value.slice(start, start + DIRECTORY_PAGE_SIZE)
})

// Keep the current page within range if the underlying list shrinks.
watch(totalPages, (total) => {
	if (page.value > total) page.value = total
})

// Add handlers
const addStream = (stream) => {
	if (streamDisabled(stream)) return
	emit('add-stream', stream)
}

// Files (from upload dirs / live-to-vod folders) are forwarded as-is; the
// directory row already tags them with the right _playoutUpload flag.
const onAddFile = (file) => emit('add-file', file)

const close = () => {
	internalVisible.value = false
}

const confirming = ref(false)
const onConfirm = async () => {
	if (!props.playoutId) {
		internalVisible.value = false
		return
	}
	confirming.value = true
	try {
		await store.dispatch('Playout/setSchedule', props.playoutId)
		notify({ group: 'success', text: 'Sources added to schedule' })
		internalVisible.value = false
	} catch (err) {
		notify({ group: 'error', text: err?.message || 'Failed to save schedule' })
	} finally {
		confirming.value = false
	}
}

const headerSubtitle = computed(() =>
	activeTab.value === 'livestreams' ? 'Please select a livestream' : 'Please select videos',
)

const sourceTabItemClasses = (isActive) =>
	[
		'h-8 p-2 justify-center hover:no-underline rounded text-surface-9 hover:text-white',
		'text-nowrap leading-none text-tiny flex items-center w-full',
		isActive ? 'bg-surface-5' : 'bg-surface-3 hover:bg-surface-5',
	].join(' ')
</script>

<template>
	<ModalBox
		v-model="internalVisible"
		title="Select video source"
		size="md"
		scroll-body
		mobile-placement="bottom"
		mobile-height-class="max-lg:h-[85dvh] max-lg:max-h-[85dvh]"
		wrapper-classes="overflow-x-hidden lg:!h-[min(680px,85vh)] !overflow-y-hidden"
		@close="close"
	>
		<section class="px-4 lg:px-6 pb-6 h-full min-h-0 flex flex-col overflow-hidden">
			<div class="shrink-0">
				<div class="border-t border-surface-3 -mx-4 lg:-mx-6 mb-4" />

				<TabGroup
					type="custom"
					custom-classes="grid grid-cols-2 w-full mb-4 !p-1 rounded bg-surface-3"
				>
					<TabItem
						centered
						label="Livestreams"
						:is-active="activeTab === 'livestreams'"
						:custom-classes="sourceTabItemClasses(activeTab === 'livestreams')"
						@click="activeTab = 'livestreams'"
					/>
					<TabItem
						centered
						label="Uploaded Videos"
						:is-active="activeTab === 'uploaded'"
						:custom-classes="sourceTabItemClasses(activeTab === 'uploaded')"
						@click="activeTab = 'uploaded'"
					/>
				</TabGroup>

				<p class="text-sm text-surface-7 m-0 mb-3">{{ headerSubtitle }}</p>
			</div>

			<!-- Livestreams tab -->
			<template v-if="activeTab === 'livestreams'">
				<div
					v-if="!livestreamsList.length"
					class="flex-1 min-h-0 text-tiny text-surface-7 text-center py-8 bg-surface-2 rounded-md"
				>
					No livestreams available.
				</div>
				<ul
					v-else
					class="flex-1 min-h-0 overflow-y-auto flex flex-col gap-2 [scrollbar-gutter:stable]"
				>
					<li
						v-for="stream in pagedLivestreams"
						:key="stream._id"
						class="flex flex-wrap sm:flex-nowrap items-center gap-3 p-3 rounded-md bg-surface-3"
					>
						<icon-base class="shrink-0 text-surface-8" :width="23" :height="17" :width-view-box="23"
							:height-view-box="17">
							<icon-livestream-camera />
						</icon-base>
						<div class="flex-1 min-w-0">
							<p class="text-sm text-white m-0 truncate" :title="stream.name">{{ stream.name }}</p>
							<div v-if="streamAddCount(stream) > 0" class="mt-0.5">
								<Badge
									type="enabled"
									size="xs"
									pill
									inline
									class="!px-2 !py-px h-4 shrink-0 whitespace-nowrap gap-1"
								>
									<icon-base :width="10" :height="10">
										<icon-checkmark />
									</icon-base>
									{{ streamAddCount(stream) === 1 ? 'In schedule' : `In schedule · ${streamAddCount(stream)}x` }}
								</Badge>
							</div>
						</div>
						<AddWithFeedback
							:count="streamAddCount(stream)"
							:show-count="false"
							:disabled="streamDisabled(stream)"
							disabled-tooltip="This stream is set as destination and cannot be added"
							success-label="Added to schedule"
							@add="addStream(stream)"
						/>
					</li>
				</ul>

				<PlayoutPagination
					v-if="livestreamsList.length"
					v-model="page"
					:total-pages="totalPages"
					class="shrink-0 !mt-3"
				/>
			</template>

			<!-- Uploaded Videos tab — folder hierarchy (upload dirs + live-to-vod) -->
			<template v-else>
				<div
					v-if="!hasDirectories"
					class="flex-1 min-h-0 text-tiny text-surface-7 text-center py-8 bg-surface-2 rounded-md"
				>
					No uploaded videos available.
				</div>
				<ul
					v-else
					class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-2 [scrollbar-gutter:stable]"
				>
					<li
						v-for="entry in pagedDirectories"
						:key="entry.key"
						class="p-3 rounded-md bg-surface-3"
					>
						<PlayoutFilesDirectoryRow
							class="!py-0"
							:dir="entry.dir"
							:playout-id="playoutId"
							:is-live-to-vod="entry.isLiveToVod"
							hide-delete
							@add-file="onAddFile"
						/>
					</li>
				</ul>
				<PlayoutPagination
					v-if="hasDirectories"
					v-model="page"
					:total-pages="totalPages"
					class="shrink-0 !mt-3"
				/>
			</template>
		</section>
	</ModalBox>
</template>

<style scoped></style>
